<template>
  <div class="login-form">
    <template v-if="loginMode === 'link' && linkSent">
      <div class="registered">
        <img src="../../../assets/icons/icon-done-2.svg" :alt="String($t('alt.complete_successfully'))">
        <h2 class="registered__title">{{ $t('loginForm.checkInbox2') }}</h2>
        <div class="registered__info">
          <v-row class="mx-0 mt-0 d-flex flex-column">
            <v-col class="px-0 py-0">
              <p class="registered__info-text"
                 v-html="$t('loginForm.confirmationSentLink', { email: form.email })" />
              <p class="registered__check-text">{{ $t('loginForm.checkInboxLink') }}</p>
            </v-col>

            <v-col class="px-0 py-0" style="text-align: center; margin-top: 80px">
              <p class="registered__resend-text">
                {{ $t('loginForm.notReceived') }}
                <span class="resend-link" @click="resendLink">{{ $t('loginForm.resendLink') }}</span>
              </p>

              <p class="registered__back-text">
                <span @click="linkSent = false" style="color: #A0AEC0; cursor: pointer;">
                  {{ $t('loginForm.backToLogin') }}
                </span>
              </p>
            </v-col>
          </v-row>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="login-form__title">
        {{ loginMode === 'link' ? $t('loginForm.login') : $t('loginForm.passwordLogin') }}
      </div>
      <v-form ref="form" v-model="valid" lazy-validation id="authForm" class="mt-9" autocomplete="off">
        <v-row class="mx-0 my-0">
          <v-col class="py-0 px-0">
            <div class="label">{{ $t('loginForm.email') }}</div>
            <Input
              ref="emailInput"
              autocomplete="off"
              input-class="login"
              outlined
              height="56"
              radius="16"
              tabindex="1"
              background-color="#EDF2F7"
              :placeholder="String($t('loginForm.emailPlaceHolder'))"
              :rules="loginEmailRules"
              @input="onInput('email', $event)"
              @enter="preLocalAuth"
            />
          </v-col>
        </v-row>
        <v-row v-if="loginMode === 'password'" class="mx-0 my-0">
          <v-col class="py-0 px-0" style="position: relative">
            <div class="label">{{ $t('loginForm.password') }}</div>
            <Input
              ref="passwordInput"
              autocomplete="off"
              input-class="login"
              outlined
              height="56"
              radius="16"
              tabindex="2"
              background-color="#EDF2F7"
              :placeholder="String($t('loginForm.pwdPlaceHolder'))"
              :type="showPassword ? 'text' : 'password'"
              :rules="loginPwdRules"
              @input="onInput('password', $event)"
              @enter="preLocalAuth"
            />
            <div class="icon">
            <span
              :class="{
                'show-pass': showPassword,
                'hide-pass': !showPassword,
              }"
              @click="toggleShow"
            ></span>
            </div>
          </v-col>
        </v-row>

        <div v-if="loginMode === 'password'" class="login-form__forgot-link d-flex px-3 pb-4">
          {{ $t('loginForm.forgotYourPwd') }}
          <Link to="/sign-in" class="ml-1" color="#668BFF" @click.native="openForgot">{{ $t('loginForm.resetPwd') }}</Link>
        </div>

        <v-row class="my-0 mx-0 mt-5">
          <v-col class="py-0 px-0">
            <div v-if="commonError" style="line-height: 12px;font-size: 12px;color: #ff5252">
              {{ commonError }}
            </div>
            <div class="">
              <Button dark btn-bg="dark-green" tabindex="3" width="100%" height="56" :styles="{ 'font-weight': '600' }" @click.native="preLocalAuth">
                {{ loginMode === 'link' ? $t('loginForm.requestLoginLink') : $t('loginForm.login') }}
              </Button>
            </div>
          </v-col>
        </v-row>
      </v-form>
      <br>
      <div class="login-form__toggle d-flex justify-center">
        <template v-if="loginMode === 'link'">
          {{ $t('loginForm.or') }}
          <Link to="/sign-in" class="ml-1" color="#668BFF" @click.native.prevent="switchLoginMode">
            {{ $t('loginForm.loginWithPassword') }}
          </Link>
        </template>
        <template v-else>
          {{ $t('loginForm.or') }}
          <Link to="/sign-in" class="ml-1" color="#668BFF" @click.native.prevent="switchLoginMode">
            {{ $t('loginForm.loginWithLink') }}
          </Link>
        </template>
      </div>
    </template>
    <div v-if="loading" class="loading d-flex justify-center px-2 py-2 align-center">
      <v-progress-circular
        indeterminate
        color="#008C89"
      ></v-progress-circular>
    </div>
    <forgot-password v-if="dialogs.forgot.isOpen" />
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import Validation from '@/mixins/ValidationMixin'
import Button from '@/components/elements/Button'
import Input from '@/components/elements/Input'
import Link from '@/components/elements/Link'
import ForgotPassword from '@/components/layouts/dialogs/forgot-password'
import { MIN_PASS_LENGTH } from '@/constants/const'
import { loginEmailRules, loginPwdRules } from '@/utils/validation'
import { trackEvent } from '@/utils/analytics'

export default {
  name: 'login-form',
  components: { Button, Input, Link, ForgotPassword },
  mixins: [Validation],
  data: () => ({
    valid: true,
    form: {
      email: 'twst@gmail.com',
      password: ''
    },
    commonError: null,
    isError: null,
    showPassword: false,
    loading: false,
    loginMode: 'link',
    linkSent: true,
    loginEmailRules: [],
    loginPwdRules: []
  }),
  computed: {
    ...mapState({
      dialogs: state => state.dialogs
    })
  },
  mounted () {
    this.setLocalizedTitle()

    this.loginMode = 'link'
    this.linkSent = false

    if (this.$store.state.token) {
      this.logout()
    }
    this.$emit('link-sent-state', this.linkSent)

    this.loginEmailRules = loginEmailRules()
    this.loginPwdRules = loginPwdRules()

    this.$nextTick(() => {
      this.$refs.emailInput?.focus?.()
    })
  },
  methods: {
    ...mapActions(['logout', 'login', 'loginWithLink']),
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),

    setLocalizedTitle () {
      document.title = this.$t('titles.login')
    },

    onInput (field, value) {
      this.form[field] = value

      const inputRef = this.$refs[`${field}Input`]
      const isValid = inputRef?.validate?.()

      if (isValid) {
        this.isError = false
      }
    },

    preLocalAuth () {
      this.loading = true
      this.commonError = null
      this.isError = null

      if (this.loginMode === 'link') {
        this.setDynamicValidationRules({
          loginEmailRules: [
            v => !!v || this.$t('validation.requiredField'),
            v => (!v || /.+@.+\..+/.test(v)) || this.$t('validation.email')
          ],
        })
      } else {
        this.setDynamicValidationRules({
          loginEmailRules: [
            v => !!v || this.$t('validation.requiredField'),
            v => (!v || /.+@.+\..+/.test(v)) || this.$t('validation.email')
          ],
          loginPwdRules: [
            v => !!v || this.$t('validation.requiredField'),
            v => (!v || v?.length >= MIN_PASS_LENGTH) || this.$t('validation.minCharacters', { count: MIN_PASS_LENGTH })
          ]
        })
      }
      this.$nextTick(() => {
        this.auth()
      }, 200)
    },

    async auth () {
      if (!this.validate()) {
        this.loading = false
        return
      }

      try {
        if (this.loginMode === 'link') {
          await this.loginWithLink(this.form.email)
          this.linkSent = true
          this.isError = false
          trackEvent(this, 'user_login', 'Authentication', 'User Logged In', {
            locale: navigator.language,
            device_type: /mobi|android/i.test(navigator.userAgent.toLowerCase()) ? 'mobile' : 'desktop'
          })
        } else {
          const res = await this.login(this.form)

          if (!res.validationResults.isValid) {
            this.isError = true
            if (res.validationResults?.results[0].name === 'Email') {
              this.setDynamicValidationRules({
                loginEmailRules: [
                  v => !!v || this.$t('validation.requiredField'),
                  v => (!v || /.+@.+\..+/.test(v)) || this.$t('validation.email'),
                  res.validationResults.isValid || res.validationResults?.results[0].message
                ]
              })
            }
            if (res.validationResults?.results[0].name === 'Password') {
              this.setDynamicValidationRules({
                loginPwdRules: [
                  v => !!v || this.$t('validation.requiredField'),
                  v => (!v || v?.length >= MIN_PASS_LENGTH) || this.$t('validation.minCharacters', { count: MIN_PASS_LENGTH }),
                  res.validationResults.isValid || res.validationResults?.results[0].message
                ]
              })
            }
            if (res.validationResults?.results[0].name === 'Common') {
              this.commonError = res.validationResults?.results[0].message
            }
          }
          if (res.validationResults.isValid) {
            this.isError = false
            trackEvent(this, 'user_login', 'Authentication', 'User Logged In', {
              locale: navigator.language,
              device_type: /mobi|android/i.test(navigator.userAgent.toLowerCase()) ? 'mobile' : 'desktop'
            })
          }
        }

      } catch (error) {
        this.linkSent = false
        console.error('Failed to send login link:', error)
      } finally {
        this.loading = false
      }
    },

    async resendLink () {
      this.loading = true
      const MIN_LOADING_DURATION = 800

      const delay = new Promise(resolve => setTimeout(resolve, MIN_LOADING_DURATION))

      try {
        await Promise.all([
          this.loginWithLink(this.form.email),
          delay
        ])
      } catch (error) {
        this.linkSent = false
        console.error('Failed to send login link:', error)
      } finally {
        this.loading = false
      }
    },

    switchLoginMode () {
      this.loginMode = this.loginMode === 'link' ? 'password' : 'link'

      this.isError = null
      this.commonError = null
      this.linkSent = false

      this.setDynamicValidationRules({
        loginEmailRules: [
          v => !!v || this.$t('validation.requiredField'),
          v => (!v || /.+@.+\..+/.test(v)) || this.$t('validation.email')
        ],
        loginPwdRules: [
          v => !!v || this.$t('validation.requiredField'),
          v => (!v || v?.length >= MIN_PASS_LENGTH) || this.$t('validation.minCharacters', { count: MIN_PASS_LENGTH })
        ]
      })
    },

    openForgot () {
      console.log('openForgot')
      this.TOGGLE_DIALOG({ name: 'forgot', isOpen: true })
    },

    toggleShow() {
      this.showPassword = !this.showPassword
    },
  },
  watch: {
    '$i18n.locale': {
      handler(newLang, oldLang) {
        if (newLang !== oldLang) {
          this.setLocalizedTitle()

          this.$nextTick(() => {
            this.$refs.emailInput?.validate?.()
            this.$refs.passwordInput?.validate?.()
          })

          if (this.loginMode !== 'link' && this.isError === true) {
            this.login(this.form).catch(err => {
              console.error('Login failed on language change:', err)
            })
          }
        }
      },
      immediate: false
    },
    '$route' (to) {
      if (to.path === '/sign-in') {
        this.loginMode = 'link'
        this.linkSent = false
      }
    },
    'form.email' (val) {
      if (!val) this.loginEmailRules = []
    },
    'form.password' (val) {
      if (!val) this.loginPwdRules = []
    },
    loginMode(val) {
      this.$emit('update:loginMode', val)
    },
    linkSent(val) {
      this.$emit('link-sent-state', val)
    }
  }
}
</script>

<style lang="scss" scoped>
.registered {
  &__resend-text {
    font-weight: 500;
    font-size: 16px;
    color: #718096;
    line-height: 24px;
    margin: 0;

    span {
      color: #668BFF;
      cursor: pointer;
    }
  }
  &__back-text {
    position: relative;
    display: inline-flex;
    color: #A0AEC0;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    padding-top: 19px;
    cursor: pointer;
    margin: 0;

    &::after {
      content: '';
      position: absolute;
      bottom: 6px;
      left: 0;
      width: 100%;
      height: 1px;
      background-color: #A0AEC0;
    }
  }
  &__title {
    font-weight: 600;
    font-size: 28px;
    color: #2D3748;
    line-height: 38px;
    padding-bottom: 20px;
    margin-top: 25px;
  }
  &__info-text {
    font-weight: 400;
    font-size: 16px;
    color: #2D3748;
    line-height: 26px;
    margin: 0;

    strong {
      font-weight: 600;
    }
  }

  &__check-text {
    font-weight: 400;
    font-size: 16px;
    color: #2D3748;
    line-height: 26px;
    padding-top: 25px;
    margin: 0;
  }
}
  .loading {
    min-height: 100px;
    position: absolute;
    height: 87%;
    width: 110%;
    top: 55px;
    left: -20px;
    background: rgba(213, 213, 213, 0.5);
    border-radius: 16px;
  }
  .label {
    font-size: 14px;
    font-weight: 500;
    color: #2D3748;
    padding-bottom: 8px;
  }
  .login-form {
    width: 100%;
    position: relative;
    &__title {
      font-weight: 500;
      font-size: 36px;
      color: #2D3748;
      text-align: center;
    }
    &__sign-up-link,
    &__forgot-link {
      font-weight: 400;
      font-size: 16px;
      color: #72767F;
      white-space: nowrap;
    }
    &__toggle {
      font-weight: 500;
      font-size: 16px;
      color: #72767F;
    }
    .icon {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 24px;

      span {
        display: inline-block;
        width: 22px;
        height: 22px;
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center;
        margin-top: 10px;
        cursor: pointer;

        &.show-pass {
          background-image: url('@/assets/icons/show.svg');
        }

        &.hide-pass {
          background-image: url('@/assets/icons/hide.svg');
        }
      }
    }
  }
  ::v-deep .v-text-field__details {
    padding-top: 5px !important;
  }
  @media screen and (max-width: 500px) {
    .login-form {
      &__sign-up-link {
        display: block !important;
        word-break: break-word !important;
        width: 100% !important;
        white-space: unset !important;
        .link {
          display: contents !important;
        }
      }
      &__forgot-link {
        display: block !important;
        word-break: break-word !important;
        width: 100% !important;
        white-space: unset !important;
        .link {
          display: contents !important;
        }
      }
    }
  }
</style>
